import { SubscriptionTier } from "@repo/ui/pricing-hooks";
import { Button, Modal } from "@shapr3d/frontend-lib";

import { useTracker } from "../../../hooks/useTracker";
import useTranslation from "../../../hooks/useTranslation";
import { ElementClickAction, ElementClickLocation } from "../../../lib/TrackingAdapter";
import { getTierDisplayName } from "../subscriptionUtils";

import style from "./SubscriptionCancelModal.module.css";

const CancelMessage: React.FC<{
  editorSeatCount: number;
  isSubscriptionInGracePeriod: boolean;
  subscriptionExpirationDate: string;
  subscriptionTier: SubscriptionTier;
}> = ({
  editorSeatCount,
  subscriptionExpirationDate,
  isSubscriptionInGracePeriod,
  subscriptionTier,
}) => {
  const { t, m } = useTranslation();

  if (editorSeatCount > 1) {
    if (isSubscriptionInGracePeriod) {
      return <p className="expirationContainer">{t(m.TeamBilling.CancelMessageTeamGracePeriod)}</p>;
    }

    return (
      <p className="expirationContainer">
        {t(m.TeamBilling.CancelMessageTeam, {
          date: subscriptionExpirationDate,
          styledDate: (chunks: React.ReactNode) => (
            <span className={style.expirationDate}>{chunks}</span>
          ),
        })}
      </p>
    );
  }

  return (
    <p className="expirationContainer">
      {t(m.TeamBilling.CancelMessageSolo, {
        tierName: getTierDisplayName(subscriptionTier),
        date: subscriptionExpirationDate,
        styledDate: (chunks: React.ReactNode) => (
          <span className={style.expirationDate}>{chunks}</span>
        ),
      })}
    </p>
  );
};

export const SubscriptionCancelModal: React.FC<{
  open: boolean;
  isLoading: boolean;
  editorSeatCount: number;
  isSubscriptionInGracePeriod: boolean;
  subscriptionExpirationDate: string;
  subscriptionTier: SubscriptionTier;
  onClose: () => void;
  onCancelSubscription: () => void;
}> = ({
  open,
  isLoading,
  editorSeatCount,
  isSubscriptionInGracePeriod,
  subscriptionExpirationDate,
  subscriptionTier,
  onClose,
  onCancelSubscription,
}) => {
  const { t, m } = useTranslation();
  const tracker = useTracker();

  return (
    <Modal open={open} title={t(m.TeamBilling.CancelSubscription)} size="small" onClose={onClose}>
      <div className={style.modalContainer}>
        <div>
          <CancelMessage
            editorSeatCount={editorSeatCount}
            isSubscriptionInGracePeriod={isSubscriptionInGracePeriod}
            subscriptionExpirationDate={subscriptionExpirationDate}
            subscriptionTier={subscriptionTier}
          />
          <p>{t(m.TeamBilling.AreYouSureCancel)}</p>
        </div>

        <div className={style.modalButtonContainer}>
          <Button
            onClick={() => {
              tracker.trackButtonClick({
                action: ElementClickAction.CANCEL,
                location: ElementClickLocation.CANCEL_SUBSCRIPTION_MODAL,
              });
              onClose();
            }}
            disabled={isLoading}
            variant="grayscale"
          >
            {t(m.TeamBilling.NotNow)}
          </Button>
          <Button
            onClick={() => {
              onCancelSubscription();
              tracker.trackButtonClick({
                action: ElementClickAction.CONFIRM,
                location: ElementClickLocation.CANCEL_SUBSCRIPTION_MODAL,
              });
            }}
            disabled={isLoading}
            variant="red"
          >
            {t(m.TeamBilling.CancelSubscription)}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
